import React, { useState } from "react";
import { fileReport } from "../lib/api";

/**
 * ReportImageModal — minimal confirm dialog for the inline "Report image"
 * CTA on player / team cards. Pre-selects NSFW as the reason since this is
 * specifically about the visual content of an uploaded image.
 *
 * Props:
 *   open: bool
 *   target: { user_id, name, team_id?, team_name? }  — at least one of user_id/team_id
 *   imageKind: 'player_pfp' | 'player_banner' | 'team_pfp' | 'team_banner'
 *   onClose, onToast
 */
export function ReportImageModal({ open, target, imageKind, onClose, onToast }) {
  const [details, setDetails] = useState("");
  const [reason, setReason] = useState("nsfw");
  const [busy, setBusy] = useState(false);

  if (!open || !target) return null;

  const subjectName =
    imageKind?.startsWith("team_")
      ? target.team_name || "team image"
      : target.name || "player image";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await fileReport(target.user_id, reason, details, {
        image_kind: imageKind,
        target_team_id: target.team_id,
      });
      onToast?.(`🚩 Reported. Mods will review the image.`);
      onClose?.();
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Couldn't file the report.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="report-image-modal"
      className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-md p-4 flex items-center justify-center"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <form
        onSubmit={submit}
        className="glass-strong rounded-3xl w-full max-w-md p-6 border-2 border-[#14b8a6]/60"
      >
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-flag mr-1" />REPORT IMAGE
        </p>
        <p className="font-display text-xl mt-1">{subjectName}</p>
        <p className="text-[11px] text-white/55 mt-1">
          Use this if the {imageKind?.includes("banner") ? "banner" : "profile picture"}{" "}
          shows sexual content, gore, hate symbols, or other policy violations.
          The image is checked by mods within 24h. Abuse of reports may affect
          your own account.
        </p>

        <fieldset className="mt-4 space-y-2" data-testid="report-image-reason">
          <legend className="text-[10px] tracking-[3px] text-[#a3a3ac]">REASON</legend>
          {[
            { code: "nsfw",         label: "NSFW / sexual content" },
            { code: "harassment",   label: "Hate symbol / harassment imagery" },
            { code: "impersonation",label: "Impersonating someone else" },
            { code: "other",        label: "Other (please describe)" },
          ].map((r) => (
            <label
              key={r.code}
              className={`flex items-center gap-2 rounded-xl border p-2.5 text-xs cursor-pointer transition ${
                reason === r.code
                  ? "border-[#14b8a6]/60 bg-[#14b8a6]/10"
                  : "border-white/10 bg-black/30 hover:border-white/30"
              }`}
            >
              <input
                type="radio"
                name="report-image-reason"
                value={r.code}
                checked={reason === r.code}
                onChange={() => setReason(r.code)}
                data-testid={`report-image-reason-${r.code}`}
                className="accent-[#14b8a6]"
              />
              <span>{r.label}</span>
            </label>
          ))}
        </fieldset>

        <label className="block mt-4">
          <span className="text-[10px] tracking-[3px] text-[#a3a3ac]">DETAILS (optional)</span>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            rows={3}
            maxLength={500}
            data-testid="report-image-details"
            className="mt-1 w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-sm text-white"
            placeholder="What's wrong with this image?"
          />
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="report-image-cancel"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl border border-white/15 text-white/70 hover:text-white"
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="report-image-submit"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl bg-[#14b8a6] text-black font-bold hover:bg-[#ff8585] disabled:opacity-50"
          >
            <i className="fas fa-flag mr-1" />{busy ? "SENDING…" : "FILE REPORT"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ReportImageModal;
